/**
 * Auto-loading "Load more" for FacetWP-driven feeds.
 *
 * Every FacetWP "Load more" button on the page (.facetwp-load-more) is watched
 * with an IntersectionObserver and auto-clicked as it scrolls into view, so
 * archives, the Post Feed block and news feeds all reveal the next batch
 * without a manual click. The button stays in the DOM as a keyboard / no-JS
 * fallback, and clicking it manually still works.
 *
 * Guards:
 *   - AUTO_LOAD_LIMIT consecutive auto-loads per "approach" of the button; the
 *     count resets whenever the button leaves the viewport, so continued
 *     scrolling keeps loading while a broken state can't spin forever.
 *   - FacetWP replaces the button on each load, so we re-scan on facetwp-loaded.
 *
 * SearchWP + other facets are handled by FacetWP itself — clicking the button
 * runs the same query FacetWP would, including the active search/filters.
 */

const AUTO_LOAD_LIMIT = 3;
// Start loading when the button is this far below the viewport (px).
const ROOT_MARGIN = '600px';

let observer = null;
let listenersBound = false;

/**
 * Stable per-feed host for the auto-load counter. FacetWP replaces the button's
 * markup on each load, but the enclosing facet element persists.
 *
 * @param {HTMLElement} btn The load-more button.
 * @return {HTMLElement} Element to store the allowance on.
 */
function allowanceHost( btn ) {
	return btn.closest( '.facetwp-facet' ) || btn.parentElement || btn;
}

/**
 * IntersectionObserver callback — auto-click on enter, reset allowance on leave.
 *
 * @param {IntersectionObserverEntry[]} entries Observed entries.
 */
function onIntersect( entries ) {
	entries.forEach( ( entry ) => {
		const btn  = entry.target;
		const host = allowanceHost( btn );
		if ( ! entry.isIntersecting ) {
			host.dataset.autoLoads = '0';
			return;
		}
		const count = parseInt( host.dataset.autoLoads || '0', 10 );
		if ( count >= AUTO_LOAD_LIMIT ) {
			return;
		}
		host.dataset.autoLoads = String( count + 1 );
		btn.click();
	} );
}

/**
 * (Re)observe every load-more button currently in the DOM.
 */
function observeButtons() {
	if ( typeof IntersectionObserver === 'undefined' ) {
		return;
	}
	if ( observer ) {
		observer.disconnect();
	}
	observer = new IntersectionObserver( onIntersect, { rootMargin: ROOT_MARGIN } );
	document.querySelectorAll( '.facetwp-load-more' ).forEach( ( btn ) => {
		// A feed can opt out of auto-loading (e.g. the Post Feed block's
		// "Auto-load on scroll" toggle off): leave the button as a manual click.
		if ( btn.closest( '[data-auto-load="false"]' ) ) {
			return;
		}
		observer.observe( btn );
	} );
}

/**
 * Initialise auto-loading for every FacetWP load-more button on the page.
 */
export function initInfiniteScroll() {
	if ( ! listenersBound ) {
		listenersBound = true;
		// A manual click refreshes the allowance for that feed.
		document.addEventListener( 'click', ( event ) => {
			const btn = event.target.closest( '.facetwp-load-more' );
			if ( btn ) {
				allowanceHost( btn ).dataset.autoLoads = '0';
			}
		} );
		// FacetWP swaps the template + button on every load / filter change.
		document.addEventListener( 'facetwp-loaded', observeButtons );
	}
	observeButtons();
}
